import {
  getImageMetadata,
  PdfAnnotationSubtype,
  PdfPageFlattenResult,
} from "@embedpdf/models";
import type {
  PdfDocumentObject,
  PdfEngine,
  PdfStampAnnoObject,
} from "@embedpdf/models";

export type PdfInputFormat = "pdf" | "jpeg" | "png";
export const PDF_IMPORT_ACCEPT =
  "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";
export const MAX_PDF_BYTES = 50 * 1024 * 1024;
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
export const MAX_IMAGE_PIXELS = 13_000_000;
export const MAX_IMAGE_DIMENSION = 6000;

interface ImportedPdf {
  bytes: Uint8Array;
  fileName: string;
  inputFormat: PdfInputFormat;
}

function checkPixels(width: number, height: number) {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1
  )
    throw new Error("This image is damaged. Choose another JPG or PNG.");
  if (
    width > MAX_IMAGE_DIMENSION ||
    height > MAX_IMAGE_DIMENSION ||
    width * height > MAX_IMAGE_PIXELS
  )
    throw new Error(
      "Image is too large. Choose a photo up to 13 megapixels and 6000 pixels per side.",
    );
}

export async function importPdfFile(
  file: File,
  engine: PdfEngine<Blob> | null,
): Promise<ImportedPdf> {
  if (!file.size)
    throw new Error("That file is empty. Choose a PDF, JPG or PNG.");
  if (file.size > MAX_PDF_BYTES)
    throw new Error(
      "File is too large. PDFs can be up to 50MB; images up to 15MB.",
    );
  const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
  const pdf = new TextDecoder("latin1").decode(head).includes("%PDF-");
  const png =
    head[0] === 137 &&
    head[1] === 80 &&
    head[2] === 78 &&
    head[3] === 71 &&
    head[4] === 13 &&
    head[5] === 10 &&
    head[6] === 26 &&
    head[7] === 10;
  const jpeg = head[0] === 255 && head[1] === 216 && head[2] === 255;
  const inputFormat: PdfInputFormat | null = png
    ? "png"
    : jpeg
      ? "jpeg"
      : pdf
        ? "pdf"
        : null;
  const expected = /\.(pdf|png|jpe?g)$/i
    .exec(file.name)?.[1]
    .toLowerCase()
    .replace("jpg", "jpeg");
  const mime = file.type.toLowerCase();
  const formats: Record<string, string> = {
    "application/pdf": "pdf",
    "application/x-pdf": "pdf",
    "image/png": "png",
    "image/jpeg": "jpeg",
    "image/jpg": "jpeg",
  };
  const declared = formats[mime];
  if (!inputFormat) {
    if (expected === "pdf")
      throw new Error(
        "This file isn't a valid PDF. It may be corrupted or a different format.",
      );
    throw new Error(
      "Choose a valid PDF, JPG or PNG. HEIC and other formats aren't supported.",
    );
  }
  if (
    (expected && expected !== inputFormat) ||
    (declared && declared !== inputFormat) ||
    /\.heic$/i.test(file.name) ||
    (mime && mime !== "application/octet-stream" && !declared)
  )
    throw new Error(
      "The file contents don't match its format. Choose the original PDF, JPG or PNG.",
    );
  if (inputFormat === "pdf")
    return {
      bytes: new Uint8Array(await file.arrayBuffer()),
      fileName: file.name,
      inputFormat,
    };
  if (file.size > MAX_IMAGE_BYTES)
    throw new Error("Image is too large. Choose a JPG or PNG up to 15MB.");
  if (!engine)
    throw new Error("The editor is still loading. Try again in a moment.");
  let metadata: ReturnType<typeof getImageMetadata>;
  try {
    metadata = getImageMetadata(await file.arrayBuffer());
  } catch {
    throw new Error("This image is damaged. Choose another JPG or PNG.");
  }
  if (!metadata || metadata.mimeType === "application/pdf")
    throw new Error("This image is damaged. Choose another JPG or PNG.");
  checkPixels(metadata.width, metadata.height);

  let bitmap: ImageBitmap | null = null;
  let image: HTMLImageElement | null = null;
  let url: string | null = null;
  let document: PdfDocumentObject | null = null;
  const canvas = window.document.createElement("canvas");
  try {
    let source: CanvasImageSource;
    let width: number;
    let height: number;
    try {
      if (typeof createImageBitmap === "function") {
        bitmap = await createImageBitmap(file, {
          imageOrientation: "from-image",
        });
        source = bitmap;
        width = bitmap.width;
        height = bitmap.height;
      } else {
        url = URL.createObjectURL(file);
        image = new Image();
        image.src = url;
        await image.decode();
        source = image;
        width = image.naturalWidth;
        height = image.naturalHeight;
      }
    } catch {
      throw new Error(
        "This image couldn't be opened. Choose another JPG or PNG.",
      );
    }
    checkPixels(width, height);
    const scale = Math.min(
      1,
      2400 / Math.max(width, height),
      Math.sqrt(4_000_000 / (width * height)),
    );
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    if (!context)
      throw new Error(
        "This browser couldn't convert the image. Please try again.",
      );
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    const normalized = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error("Image conversion failed. Please try again.")),
        "image/jpeg",
        0.92,
      ),
    );
    const response = await fetch(
      `/templates/photo-${width > height ? "landscape" : "portrait"}.pdf`,
    );
    if (!response.ok)
      throw new Error("Couldn't load the photo page. Please try again.");
    document = await engine
      .openDocumentBuffer({
        id: `photo-import-${crypto.randomUUID()}`,
        content: await response.arrayBuffer(),
      })
      .toPromise();
    if (document.pages.length !== 1)
      throw new Error("Couldn't create a photo page. Please try again.");
    const page = document.pages[0];
    const fit = Math.min(page.size.width / width, page.size.height / height);
    const size = { width: width * fit, height: height * fit };
    const stamp: PdfStampAnnoObject = {
      id: crypto.randomUUID(),
      type: PdfAnnotationSubtype.STAMP,
      pageIndex: 0,
      flags: ["print"],
      rect: {
        origin: {
          x: (page.size.width - size.width) / 2,
          y: (page.size.height - size.height) / 2,
        },
        size,
      },
    };
    const created = await engine
      .createPageAnnotation(document, page, stamp, {
        data: await normalized.arrayBuffer(),
        mimeType: "image/jpeg",
      })
      .toPromise();
    if (
      !created ||
      (await engine.flattenPage(document, page).toPromise()) !==
        PdfPageFlattenResult.Success
    )
      throw new Error("Image conversion failed. Please try again.");
    const bytes = new Uint8Array(await engine.saveAsCopy(document).toPromise());
    if (
      bytes.length > MAX_PDF_BYTES ||
      new TextDecoder().decode(bytes.subarray(0, 5)) !== "%PDF-"
    )
      throw new Error("Image conversion failed. Please try a smaller photo.");
    return {
      bytes,
      fileName: file.name.replace(/\.[^.]+$/, "") + ".pdf",
      inputFormat,
    };
  } finally {
    bitmap?.close();
    if (image) image.src = "";
    if (url) URL.revokeObjectURL(url);
    canvas.width = 0;
    canvas.height = 0;
    if (document) await engine.closeDocument(document).toPromise();
  }
}
